import { ClearFiltersButton } from "@/components/ui/ClearFiltersButton";
import { ErrorResult } from "@/components/ui/ErrorResult";
import {
  ModalVPayment,
  ModalVPaymentRef,
} from "@/components/ui/modals/ModalVPayment";
import useColumnSearch from "@/hooks/table/useColumnSearch";
import useTableFilters from "@/hooks/table/useTableFilters";
import { formatPrice } from "@/lib/utils";
import { usePayments } from "@/services/payment/queries";
import { Payment } from "@/types/payment";
import {
  DownloadOutlined,
  EyeOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { Button, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useRef } from "react";
import { useExportPayments } from "./payments/useExportPayments";

export const paymentMethodLabel: Record<
  string,
  { label: string; color: string }
> = {
  cash: { label: "Efectivo", color: "green" },
  card: { label: "Tarjeta", color: "blue" },
  credit: { label: "Crédito", color: "orange" },
};

interface PaymentsPrivateProps {
  filterToday?: boolean;
}

export const PaymentsPrivate = ({
  filterToday = false,
}: PaymentsPrivateProps) => {
  const { tableParams, tableKey, resetFilters, handleTableChange } =
    useTableFilters({
      initialFilters: filterToday
        ? {
            created_at: ["today"],
          }
        : {},
    });

  const { data, isLoading, isError, error, isFetching, refetch } =
    usePayments(tableParams);

  const {
    contextHolder: exportContextHolder,
    generateExcel,
    loadingButtonExport,
  } = useExportPayments({ tableParams });

  const modalV = useRef<ModalVPaymentRef>(null);

  const { getColumnSearchProps } = useColumnSearch<Payment>();

  const columns: ColumnsType<Payment> = [
    {
      title: "N° Ticket",
      dataIndex: "number_ticket",
      key: "number_ticket",
      ...getColumnSearchProps("number_ticket"),
      render: (text: string) =>
        text ? text : <Tag color="red">Sin N° Ticket</Tag>,
    },
    {
      title: "Mesa",
      dataIndex: "table_number",
      key: "table_number",
      ...getColumnSearchProps("table_number"),
      render: (text: string) => (text ? text : <Tag color="red">Sin mesa</Tag>),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (text: number) => formatPrice(text),
      sorter: true,
    },
    {
      title: "Método de pago",
      dataIndex: "method",
      key: "method",
      render: (method) => {
        const config = paymentMethodLabel[method] ?? {
          label: method,
          color: "default",
        };

        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: [
        {
          text: "Efectivo",
          value: "cash",
        },
        {
          text: "Tarjeta",
          value: "card",
        },
        {
          text: "Crédito",
          value: "credit",
        },
      ],
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "paid" ? "green" : "orange"}>
          {status === "paid" ? "Pagado" : "Pendiente"}
        </Tag>
      ),
      filters: [
        { text: "Pagado", value: "paid" },
        { text: "Pendiente", value: "pending" },
      ],
    },
    {
      title: "N° Factura",
      dataIndex: "invoice_number",
      key: "invoice_number",
      ...getColumnSearchProps("invoice_number"),
      render: (text?: string) => (text ? <Tag color="blue">{text}</Tag> : "-"),
    },
    {
      title: "Fecha de pago",
      dataIndex: "created_at",
      key: "created_at",
      filters: [
        { text: "Hoy", value: "today" },
        { text: "Ayer", value: "yesterday" },
        { text: "Esta semana", value: "week" },
        { text: "Este mes", value: "month" },
        { text: "Mes pasado", value: "last_month" },
      ],
      filteredValue: tableParams.filters.created_at ?? null,
      filterMultiple: false,
      render: (created_at?: string) =>
        created_at ? new Date(created_at).toLocaleString("es-CL") : "Sin fecha",
    },
    {
      title: "Acciones",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (id) => (
        <Space size="middle">
          <Tooltip placement="top" className="cursor-pointer" title="Ver">
            <button
              onClick={() => {
                modalV.current?.childFunction(id);
              }}
            >
              <EyeOutlined className="text-lg" />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isError) {
    return <ErrorResult error={error} refetch={refetch} />;
  }

  return (
    <>
      {exportContextHolder}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-3xl font-bold sm:text-4xl">Historial de Caja</h2>
        {/* exportar y total de ventas*/}
        <div className="rounded-xl border border-[#1A2328] bg-[#070B0E] ml-auto  px-5 py-2 max-w-fit mb-3">
          <div className="flex items-center gap-2">
            <LineChartOutlined className="text-lg" />
            <p className="text-sm text-gray-400">Total recaudado</p>
          </div>

          <h2 className="mt-1 text-xl font-bold text-center text-white">
            {formatPrice(data?.total_payment_amount ?? 0)}
          </h2>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-3">
        <div className="ml-auto max-w-fit">
          <span className="text-xs px-2 py-1 rounded-full bg-[#070B0E] text-gray-300 border border-[#1A2328]">
            {data?.total ?? 0} registros
          </span>
        </div>

        <Button
          type="default"
          icon={<DownloadOutlined />}
          loading={loadingButtonExport}
          onClick={() => generateExcel()}
          className="flex items-center gap-1 font-medium"
        >
          Descargar Excel
        </Button>

        <ClearFiltersButton onClear={resetFilters} />
      </div>

      <Table<Payment>
        columns={columns}
        dataSource={data?.items}
        key={tableKey}
        pagination={{
          current: tableParams?.pagination.current,
          pageSize: tableParams?.pagination.pageSize,
          total: data?.total,
        }}
        onChange={handleTableChange}
        loading={isLoading || isFetching}
        scroll={{ x: 1000 }}
      />

      <ModalVPayment ref={modalV} />
    </>
  );
};
